import React, { useState, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  MdAdd,
  MdArrowBack,
  MdArticle,
  MdCheckCircle,
  MdDeleteForever,
  MdDescription,
  MdEdit,
  MdPauseCircle,
  MdSearch,
  MdVisibility,
} from 'react-icons/md';
import { postData, getData } from '../../Webservice/Webservice';

const FONT = 'Mulish, sans-serif';
const PAGE_SIZE = 10;

const policyTypes = [
  { value: 'privacy', label: 'Privacy Policy', color: '#6366f1' },
  { value: 'terms', label: 'Terms & Conditions', color: '#8b5cf6' },
  { value: 'return_refund', label: 'Return & Refund Policy', color: '#10b981' },
  { value: 'shipping', label: 'Shipping Policy', color: '#0ea5e9' },
  { value: 'payment', label: 'Payment Policy', color: '#14b8a6' },
  { value: 'cancellation', label: 'Cancellation Policy', color: '#f59e0b' },
  { value: 'useraccount', label: 'User Policy', color: '#ec4899' },
  { value: 'liability_limitation', label: 'Liability Limitation', color: '#ef4444' },
];
const typeOf = (v) => policyTypes.find((t) => t.value === v) || { label: v || 'Unknown', color: '#64748b' };

const EMPTY_FORM = { policyType: '', title: '', contentHtml: '', effectiveDate: '', summary: '', makeActive: false };

const authToken = () => {
  const raw = sessionStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-');
// Quill leaves "<p><br></p>" behind when the editor is emptied
const isEmptyHtml = (html) => !html || (html.replace(/<[^>]*>/g, '').trim() === '' && !/<img/i.test(html));

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link', 'image'],
    ['clean'],
  ],
};
const quillFormats = ['header', 'bold', 'italic', 'underline', 'strike', 'color', 'background', 'list', 'bullet', 'indent', 'link', 'image', 'align'];

// ---------- presentational pieces (module level so inputs keep focus) ----------
const TypeChip = ({ type }) => {
  const t = typeOf(type);
  return <Chip size="small" label={t.label} sx={{ fontFamily: FONT, fontWeight: 700, height: 24, background: `${t.color}1a`, color: t.color }} />;
};
const StatusChip = ({ active }) => (
  <Chip size="small" label={active ? 'Active' : 'Inactive'} sx={{ fontFamily: FONT, fontWeight: 700, height: 24, background: active ? '#dcfce7' : '#f1f5f9', color: active ? '#166534' : '#475569' }} />
);

const Section = ({ title, hint, accent, icon: Icon, children }) => (
  <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, boxShadow: '0 4px 20px rgba(15,23,42,.04)' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
      <Box sx={{ background: `${accent}1a`, color: accent, borderRadius: '10px', p: 1, display: 'flex' }}><Icon size={18} /></Box>
      <Box>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, color: '#0f172a', lineHeight: 1.2 }}>{title}</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>{hint}</Typography>
      </Box>
    </Box>
    {children}
  </Paper>
);

const PageHeader = ({ title, subtitle, onBack, right }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <IconButton onClick={onBack} aria-label="back" sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}><MdArrowBack size={20} /></IconButton>
      <Box>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 23.5, color: '#0f172a', lineHeight: 1.2 }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>{subtitle}</Typography>}
      </Box>
    </Box>
    {right}
  </Box>
);

function AppPolicy() {
  const [view, setView] = useState('list'); // 'list' | 'form' | 'view'
  const [policies, setPolicies] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchPolicies = useCallback(async (page) => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (typeFilter) params.policyType = typeFilter;
      if (statusFilter) params.isActive = statusFilter === 'active' ? 'true' : 'false';
      if (search) params.search = search;
      const res = await getData('policy/list', params, authToken(), false);
      setPolicies(res?.policies || []);
      if (res?.stats) setStats(res.stats);
      const m = res?.meta || { page: 1, totalPages: 1, total: 0 };
      setMeta({ page: m.page, totalPages: m.totalPages, total: m.total });
      setError('');
    } catch (err) {
      setPolicies([]);
      setMeta({ page: 1, totalPages: 1, total: 0 });
      setError('Could not load policies. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter, search]);

  useEffect(() => {
    if (view === 'list') fetchPolicies(meta.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, meta.page, fetchPolicies]);

  // wait for the user to stop typing before searching
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setMeta((prev) => ({ ...prev, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const changeFilter = (setter) => (value) => {
    setter(value);
    setMeta((prev) => ({ ...prev, page: 1 }));
  };
  const hasFilters = !!(typeFilter || statusFilter || search);

  // ---------- navigation ----------
  const handleBackToList = () => {
    setView('list');
    setSelectedPolicy(null);
    setIsEditMode(false);
    setFormData(EMPTY_FORM);
    setErrors({});
  };
  const handleOpenForm = () => {
    setIsEditMode(false);
    setSelectedPolicy(null);
    setFormData(EMPTY_FORM);
    setErrors({});
    setView('form');
  };
  const handleEditPolicy = (policy) => {
    setSelectedPolicy(policy);
    setFormData({
      policyType: policy.policyType || '',
      title: policy.title || '',
      contentHtml: policy.contentHtml || '',
      effectiveDate: policy.effectiveDate ? new Date(policy.effectiveDate).toISOString().split('T')[0] : '',
      summary: policy.summary || '',
      makeActive: !!policy.isActive,
    });
    setErrors({});
    setIsEditMode(true);
    setView('form');
  };
  const handleViewPolicy = (policy) => {
    setSelectedPolicy(policy);
    setView('view');
  };

  // ---------- form ----------
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!formData.policyType) next.policyType = 'Choose the policy type';
    if (!formData.title.trim()) next.title = 'Title is required';
    if (!formData.effectiveDate) next.effectiveDate = 'Effective date is required';
    if (!formData.summary.trim()) next.summary = 'Add a short summary';
    if (isEmptyHtml(formData.contentHtml)) next.contentHtml = 'The policy content cannot be empty';
    setErrors(next);
    if (Object.keys(next).length) return;

    setSaving(true);
    try {
      const payload = { ...formData, title: formData.title.trim(), summary: formData.summary.trim() };
      if (isEditMode) await postData(`policy/update/${selectedPolicy._id || selectedPolicy.id}`, payload, authToken());
      else await postData('policy/add', payload, authToken());
      handleBackToList();
    } catch (err) {
      setError(`Failed to ${isEditMode ? 'update' : 'add'} the policy. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await postData(`policy/delete/${deleteTarget._id || deleteTarget.id}`, {}, authToken());
      const wasView = view === 'view';
      setDeleteTarget(null);
      if (wasView) handleBackToList();
      else if (policies.length === 1 && meta.page > 1) setMeta((prev) => ({ ...prev, page: prev.page - 1 }));
      else fetchPolicies(meta.page);
    } catch (err) {
      setError('Failed to delete the policy. Please try again.');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const deleteDialog = (
    <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} PaperProps={{ sx: { borderRadius: '14px', p: 1, minWidth: 380 } }}>
      <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>Delete policy?</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontFamily: FONT }}>
          <b>{deleteTarget?.title}</b> will be permanently removed{deleteTarget?.isActive ? ' — it is the live version, so customers will have no ' + typeOf(deleteTarget?.policyType).label + ' until you activate another' : ''}.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={() => setDeleteTarget(null)} disabled={deleting} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
        <Button onClick={confirmDelete} disabled={deleting} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>
          {deleting ? 'Deleting…' : 'Yes, delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const pageSx = { p: { xs: 1.5, md: 3 }, background: '#f8fafc', minHeight: '100vh' };

  // ---------- view one policy ----------
  if (view === 'view' && selectedPolicy) {
    const t = typeOf(selectedPolicy.policyType);
    return (
      <Box sx={pageSx}>
        <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
          {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
          <PageHeader
            title={selectedPolicy.title}
            subtitle={t.label}
            onBack={handleBackToList}
            right={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button startIcon={<MdEdit />} onClick={() => handleEditPolicy(selectedPolicy)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', background: 'rgb(255 209 2)', color: 'black', px: 2.5, '&:hover': { background: 'rgb(255 190 0)' } }}>Edit</Button>
                <Button startIcon={<MdDeleteForever />} onClick={() => setDeleteTarget(selectedPolicy)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', color: '#dc2626', background: 'rgba(220,38,38,.1)', px: 2.5 }}>Delete</Button>
              </Box>
            }
          />
          <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0', borderTop: `4px solid ${t.color}`, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 2.5 }}>
            {[
              ['Status', <StatusChip key="s" active={selectedPolicy.isActive} />],
              ['Version', `v${selectedPolicy.versionNumber || 1}`],
              ['Effective date', fmtDate(selectedPolicy.effectiveDate)],
              ['Last updated', fmtDate(selectedPolicy.updatedAt)],
            ].map(([k, v]) => (
              <Box key={k}>
                <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>{k}</Typography>
                <Typography component="div" sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>{v}</Typography>
              </Box>
            ))}
          </Paper>
          {selectedPolicy.summary && (
            <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>Summary</Typography>
              <Typography sx={{ fontFamily: FONT, color: '#334155', lineHeight: 1.6 }}>{selectedPolicy.summary}</Typography>
            </Paper>
          )}
          <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 2 }}>Policy content</Typography>
            <div
              style={{ fontFamily: FONT, fontSize: 13.5, lineHeight: 1.75, color: '#334155' }}
              dangerouslySetInnerHTML={{ __html: selectedPolicy.contentHtml || '<p>No content available.</p>' }}
            />
          </Paper>
        </Box>
        {deleteDialog}
      </Box>
    );
  }

  // ---------- create / edit ----------
  if (view === 'form') {
    return (
      <Box sx={pageSx}>
        <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
          {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
          <PageHeader
            title={isEditMode ? 'Edit Policy' : 'Add Policy'}
            subtitle={isEditMode ? formData.title : 'Write the policy customers will read in the app.'}
            onBack={handleBackToList}
          />
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Section title="Policy details" hint="What this policy is and when it takes effect" accent="#6366f1" icon={MdDescription}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <TextField select fullWidth size="small" label="Policy type" name="policyType" value={formData.policyType} onChange={handleChange} required error={!!errors.policyType} helperText={errors.policyType}>
                    {policyTypes.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" label="Effective date" name="effectiveDate" type="date" value={formData.effectiveDate} onChange={handleChange} required InputLabelProps={{ shrink: true }} error={!!errors.effectiveDate} helperText={errors.effectiveDate} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Title" name="title" value={formData.title} onChange={handleChange} required error={!!errors.title} helperText={errors.title} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Summary" name="summary" value={formData.summary} onChange={handleChange} required multiline rows={2} error={!!errors.summary} helperText={errors.summary || 'One or two lines shown in the list'} />
                </Grid>
              </Grid>
            </Section>

            <Section title="Content" hint="The full text customers see" accent="#0ea5e9" icon={MdArticle}>
              <Box sx={{ '& .ql-toolbar': { borderRadius: '10px 10px 0 0', borderColor: errors.contentHtml ? '#ef4444' : '#e2e8f0', background: '#f8fafc' }, '& .ql-container': { borderRadius: '0 0 10px 10px', borderColor: errors.contentHtml ? '#ef4444' : '#e2e8f0', fontFamily: FONT, fontSize: 13.5 }, '& .ql-editor': { minHeight: 320 } }}>
                <ReactQuill
                  value={formData.contentHtml}
                  onChange={(value) => { setFormData((prev) => ({ ...prev, contentHtml: value })); if (errors.contentHtml) setErrors((prev) => ({ ...prev, contentHtml: undefined })); }}
                  modules={quillModules}
                  formats={quillFormats}
                  theme="snow"
                />
              </Box>
              {errors.contentHtml && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#dc2626', mt: 0.75 }}>{errors.contentHtml}</Typography>}
            </Section>

            <Section title="Visibility" hint="Only one version of each policy type can be live" accent="#10b981" icon={MdCheckCircle}>
              <FormControlLabel
                control={<Switch name="makeActive" checked={formData.makeActive} onChange={handleChange} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: 'rgb(255, 209, 2)' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(245, 216, 87, 1)' } }} />}
                label={<Typography sx={{ fontFamily: FONT, fontWeight: 600 }}>{isEditMode ? 'This version is live in the app' : 'Make this policy live immediately'}</Typography>}
              />
              {formData.makeActive && formData.policyType && (
                <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b', mt: 0.5 }}>
                  Any other active {typeOf(formData.policyType).label} will be switched off.
                </Typography>
              )}
            </Section>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button variant="outlined" onClick={handleBackToList} sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: 700, px: 3, py: 1, borderRadius: '10px', minWidth: 120, color: '#475569', borderColor: '#cbd5e1' }}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={saving} sx={{ fontFamily: FONT, textTransform: 'none', bgcolor: 'rgb(255 209 2)', color: 'black', px: 3, py: 1, borderRadius: '10px', minWidth: 180, fontWeight: 800, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { bgcolor: 'rgb(255 190 0)' } }}>
                {saving ? <><CircularProgress size={18} sx={{ color: 'black', mr: 1 }} />Saving…</> : isEditMode ? 'Update policy' : 'Add policy'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  // ---------- list ----------
  const kpis = [
    { label: 'Total policies', value: stats.total, icon: MdArticle, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', key: '' },
    { label: 'Active (live)', value: stats.active, icon: MdCheckCircle, gradient: 'linear-gradient(135deg,#10b981,#059669)', key: 'active' },
    { label: 'Inactive', value: stats.inactive, icon: MdPauseCircle, gradient: 'linear-gradient(135deg,#64748b,#475569)', key: 'inactive' },
  ];

  return (
    <Box sx={pageSx}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 25, color: '#0f172a' }}>App Policy</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>Legal and customer policies shown in the mobile app.</Typography>
          </Box>
          <Button
            variant="contained" startIcon={<MdAdd />} onClick={handleOpenForm}
            sx={{ fontFamily: FONT, background: 'rgb(255 209 2)', color: 'black', textTransform: 'none', borderRadius: '10px', fontWeight: 700, px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', border: '1px solid black', '&:hover': { background: 'rgb(255 209 2)', transform: 'translateY(-1px)' } }}
          >
            Add Policy
          </Button>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2, mb: 3 }}>
          {kpis.map((k) => (
            <Paper
              key={k.label} elevation={0} onClick={() => changeFilter(setStatusFilter)(k.key)}
              sx={{ p: 2.5, borderRadius: '16px', color: '#fff', background: k.gradient, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', outline: statusFilter === k.key ? '3px solid #0f172a' : 'none', outlineOffset: 2, boxShadow: '0 4px 12px rgba(15,23,42,.1)', transition: 'transform .15s', '&:hover': { transform: 'translateY(-2px)' } }}
            >
              <Box sx={{ background: 'rgba(255,255,255,.22)', borderRadius: '14px', p: 1.5, display: 'flex' }}><k.icon size={26} /></Box>
              <Box>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, opacity: 0.92, fontFamily: FONT }}>{k.label}</Typography>
                <Typography sx={{ fontSize: 25, fontWeight: 800, lineHeight: 1.2, fontFamily: FONT }}>{k.value}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>

        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '14px', border: '1px solid #e2e8f0', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small" placeholder="Search title, summary or content" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} sx={{ flex: 1, minWidth: 240 }}
            InputProps={{ startAdornment: <MdSearch size={20} style={{ marginRight: 8, color: '#94a3b8' }} /> }}
          />
          <TextField select size="small" label="Policy type" value={typeFilter} onChange={(e) => changeFilter(setTypeFilter)(e.target.value)} sx={{ minWidth: 210 }}>
            <MenuItem value="">All types</MenuItem>
            {policyTypes.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => changeFilter(setStatusFilter)(e.target.value)} sx={{ minWidth: 140 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          {hasFilters && <Button onClick={() => { setSearchInput(''); setTypeFilter(''); setStatusFilter(''); setMeta((p) => ({ ...p, page: 1 })); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Clear</Button>}
        </Paper>

        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(15,23,42,.05)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': { backgroundColor: '#f8fafc', fontWeight: 800, py: 1.75, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', fontFamily: FONT, borderBottom: '2px solid #e2e8f0' } }}>
                <TableCell>Policy</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Effective</TableCell>
                <TableCell>Summary</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>{[200, 150, 100, 240, 80, 110].map((w, j) => <TableCell key={j}><Skeleton width={w} /></TableCell>)}</TableRow>
                ))
              ) : policies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 6, textAlign: 'center' }}>
                    <Typography sx={{ fontFamily: FONT, color: '#64748b', fontWeight: 600 }}>{hasFilters ? 'No policies match your filters.' : 'No policies yet. Add your first one.'}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                policies.map((policy) => (
                  <TableRow key={policy._id || policy.id} hover onClick={() => handleViewPolicy(policy)} sx={{ cursor: 'pointer', '& td': { borderBottom: '1px solid #f1f5f9', py: 1.75, fontFamily: FONT } }}>
                    <TableCell>
                      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13, color: '#0f172a' }}>{policy.title}</Typography>
                      <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#64748b' }}>Version {policy.versionNumber || 1}</Typography>
                    </TableCell>
                    <TableCell><TypeChip type={policy.policyType} /></TableCell>
                    <TableCell><Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#475569' }}>{fmtDate(policy.effectiveDate)}</Typography></TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={policy.summary}>{policy.summary || '-'}</Typography>
                    </TableCell>
                    <TableCell><StatusChip active={policy.isActive} /></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="View" arrow>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleViewPolicy(policy); }} sx={{ color: '#4f46e5', background: 'rgba(79,70,229,.1)', borderRadius: '8px', mr: 0.75 }}><MdVisibility size={16} /></IconButton>
                      </Tooltip>
                      <Tooltip title="Edit" arrow>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditPolicy(policy); }} sx={{ color: '#ca8a04', background: '#fefce8', borderRadius: '8px', mr: 0.75 }}><MdEdit size={16} /></IconButton>
                      </Tooltip>
                      <Tooltip title="Delete" arrow>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteTarget(policy); }} sx={{ color: '#dc2626', background: 'rgba(220,38,38,.1)', borderRadius: '8px' }}><MdDeleteForever size={18} /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {meta.total > 0 && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={meta.totalPages} page={meta.page} shape="rounded" showFirstButton showLastButton
                onChange={(_, p) => setMeta((prev) => ({ ...prev, page: p }))}
                sx={{ '& .MuiPagination-ul': { flexWrap: 'nowrap' }, '& .MuiPaginationItem-root': { fontFamily: FONT, fontWeight: 600, borderRadius: '8px', '&.Mui-selected': { backgroundColor: 'rgb(255, 209, 2)', color: 'black', '&:hover': { backgroundColor: 'rgb(255, 190, 0)' } } } }}
              />
            </Box>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
              Showing {(meta.page - 1) * PAGE_SIZE + 1} to {Math.min(meta.page * PAGE_SIZE, meta.total)} of {meta.total} policies
            </Typography>
          </>
        )}
      </Box>
      {deleteDialog}
    </Box>
  );
}

export default AppPolicy;
